import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard,
  Egg,
  CircleDollarSign,
  Package,
  Users,
  ShoppingCart,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Wallet,
  Truck,
  PackageOpen,
  ClipboardList,
  ClipboardCheck,
  Clock,
  UserCircle,
  Sheet,
  Stethoscope,
  Bell,
  Banknote,
  Receipt,
  FileStack,
  MessageSquare,
  BookUser,
  ChevronDown,
  ChevronRight,
  Factory,
  Boxes,
  Store,
  CreditCard,
  Scale,
  ArrowDownCircle,
  ArrowUpCircle,
  PanelLeftClose,
  PanelLeft,
  Bird,
  Tag,
  BookOpen,
  ShoppingBag,
  Wrench,
  FolderOpen,
  Wheat,
  TrendingUp,
  Snowflake,
  Calendar,
  AlertTriangle,
  RefreshCw,
  UserMinus,
  UserPlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Custom Naira Icon component
const NairaIcon = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M6 4v16" />
    <path d="M18 4v16" />
    <path d="M6 4l12 16" />
    <path d="M4 9h16" />
    <path d="M4 15h16" />
  </svg>
);

// Collapsible Group Component
const NavGroup = ({ label, icon: Icon, items, location, onNavigate, defaultOpen = false, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const basePath = (p) => (p || '').split('?')[0];
  
  // Auto-open if any child is active
  useEffect(() => {
    const hasActiveChild = items.some(item => location.pathname === basePath(item.path));
    if (hasActiveChild && !isOpen) {
      setIsOpen(true);
    }
  }, [location.pathname, items]);
  
  const hasActiveChild = items.some(item => location.pathname === basePath(item.path));
  
  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-3 px-4 py-2.5 w-full text-sm font-medium rounded-md transition-colors ${
          hasActiveChild 
            ? 'bg-slate-800 text-white' 
            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
        }`}
      >
        <Icon className="w-5 h-5" strokeWidth={2} />
        <span className="flex-1 text-left">{label}</span>
        {badge ? (
          <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
        {isOpen ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
      
      {isOpen && (
        <div className="ml-4 pl-4 border-l border-slate-700 space-y-0.5">
          {items.map((item) => {
            const ItemIcon = item.icon;
            const isActive = location.pathname === basePath(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={onNavigate}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <ItemIcon className="w-4 h-4" strokeWidth={2} />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Single Nav Item Component
const NavItem = ({ item, location, onNavigate }) => {
  const Icon = item.icon;
  const isActive = location.pathname === item.path;
  
  return (
    <Link
      to={item.path}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onNavigate}
      className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
        isActive
          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
      }`}
    >
      <Icon className="w-5 h-5" strokeWidth={2} />
      <span className="flex-1">{item.label}</span>
      {item.badge && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </Link>
  );
};

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pendingFormsCount, setPendingFormsCount] = useState(0);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [pendingOrderRequestsCount, setPendingOrderRequestsCount] = useState(0);
  const [pendingFormsByType, setPendingFormsByType] = useState({});
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [unreadPickupNotifications, setUnreadPickupNotifications] = useState(0);
  const [unmatchedDepositsCount, setUnmatchedDepositsCount] = useState(0);
  const [farmOpsUnreadCount, setFarmOpsUnreadCount] = useState(0);

  const userRole = user?.role;
  const allowedTabs = user?.allowed_tabs || [];
  const isApproved = user?.is_approved !== false;

  // Fetch unmatched bank deposits count for admin/manager (sidebar badge on bank pages)
  useEffect(() => {
    const fetchUnmatchedDepositsCount = async () => {
      if (userRole === 'admin' || userRole === 'manager') {
        try {
          const response = await axios.get(`${API}/reconciliation-unmatched-deposits/count`, getAuthHeader());
          setUnmatchedDepositsCount(response.data.count || 0);
        } catch (error) {
          // Managers without bank access get 403 - keep badge hidden
        }
      }
    };

    fetchUnmatchedDepositsCount();
    const interval = setInterval(fetchUnmatchedDepositsCount, 60000);
    const handleChanged = () => fetchUnmatchedDepositsCount();
    window.addEventListener('unmatchedDepositsChanged', handleChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('unmatchedDepositsChanged', handleChanged);
    };
  }, [userRole]);

  // Fetch unread Daily Farm Operations Report count for admin/manager (sidebar badge)
  useEffect(() => {
    const fetchFarmOpsUnread = async () => {
      if (['admin', 'manager', 'distributor', 'staff', 'support_staff'].includes(userRole)) {
        try {
          const response = await axios.get(`${API}/farm-operations-reports-unread-count`, getAuthHeader());
          setFarmOpsUnreadCount(response.data.count || 0);
        } catch (error) {
          // ignore
        }
      }
    };
    fetchFarmOpsUnread();
    const interval = setInterval(fetchFarmOpsUnread, 60000);
    const handleSeen = () => fetchFarmOpsUnread();
    window.addEventListener('farmOpsSeen', handleSeen);
    return () => {
      clearInterval(interval);
      window.removeEventListener('farmOpsSeen', handleSeen);
    };
  }, [userRole]);

  // Fetch pending forms count for admin/manager
  useEffect(() => {
    const fetchPendingFormsCount = async () => {
      if (userRole === 'admin' || userRole === 'manager') {
        try {
          const response = await axios.get(`${API}/forms/pending-count`, getAuthHeader());
          setPendingFormsCount(response.data.count || 0);
          setPendingFormsByType(response.data.by_type || {});
        } catch (error) {
          console.error('Failed to fetch pending forms count:', error);
        }
      }
    };

    fetchPendingFormsCount();
    const interval = setInterval(fetchPendingFormsCount, 60000);
    const handleFormsSeen = () => fetchPendingFormsCount();
    window.addEventListener('formsSeen', handleFormsSeen);
    return () => {
      clearInterval(interval);
      window.removeEventListener('formsSeen', handleFormsSeen);
    };
  }, [userRole]);

  // Fetch pending order notifications count for admin/manager/distributor/staff
  useEffect(() => {
    const fetchPendingOrdersCount = async () => {
      if (userRole === 'admin' || userRole === 'manager' || userRole === 'distributor' || userRole === 'staff') {
        try {
          const response = await axios.get(`${API}/order-notifications/pending-count`, getAuthHeader());
          setPendingOrdersCount(response.data.count || 0);
        } catch (error) {
          console.error('Failed to fetch pending orders count:', error);
        }
      }
    };

    fetchPendingOrdersCount();
    const interval = setInterval(fetchPendingOrdersCount, 60000);
    return () => clearInterval(interval);
  }, [userRole]);

  // Fetch pending order requests count for admin/manager/distributor
  useEffect(() => {
    const fetchPendingOrderRequestsCount = async () => {
      if (userRole === 'admin' || userRole === 'manager' || userRole === 'distributor') {
        try {
          const response = await axios.get(`${API}/order-requests/pending-count`, getAuthHeader());
          setPendingOrderRequestsCount(response.data.count || 0);
        } catch (error) {
          console.error('Failed to fetch pending order requests count:', error);
        }
      }
    };

    fetchPendingOrderRequestsCount();
    const interval = setInterval(fetchPendingOrderRequestsCount, 60000);
    return () => clearInterval(interval);
  }, [userRole]);

  // Fetch unread messages count for customers
  useEffect(() => {
    const fetchUnreadMessagesCount = async () => {
      if (userRole === 'customer') {
        try {
          const response = await axios.get(`${API}/customer/conversations`, getAuthHeader());
          const conversations = response.data || [];
          // Count unread messages across all conversations
          const unreadCount = conversations.reduce((total, conv) => {
            const userId = user?.id;
            const isInitiator = conv.initiated_by === userId;
            return total + (isInitiator ? (conv.unread_count_sender || 0) : (conv.unread_count_recipient || 0));
          }, 0);
          setUnreadMessagesCount(unreadCount);
        } catch (error) {
          console.error('Failed to fetch unread messages count:', error);
        }
      }
    };

    fetchUnreadMessagesCount();
    const interval = setInterval(fetchUnreadMessagesCount, 30000); // Poll every 30 seconds
    
    // Listen for custom events to update badge immediately (real-time updates)
    const handleMessagesRead = () => {
      fetchUnreadMessagesCount();
    };
    
    window.addEventListener('messagesRead', handleMessagesRead);
    window.addEventListener('conversationOpened', handleMessagesRead);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('messagesRead', handleMessagesRead);
      window.removeEventListener('conversationOpened', handleMessagesRead);
    };
  }, [userRole, user?.id]);

  // Fetch unread pickup notifications for customers
  useEffect(() => {
    const fetchUnreadPickupNotifications = async () => {
      if (userRole === 'customer') {
        try {
          const response = await axios.get(`${API}/user-notifications/unread-count?type=order_pickup_completed`, getAuthHeader());
          setUnreadPickupNotifications(response.data.count || 0);
        } catch (error) {
          console.error('Failed to fetch pickup notifications count:', error);
        }
      }
    };

    fetchUnreadPickupNotifications();
    const interval = setInterval(fetchUnreadPickupNotifications, 30000);
    
    // Listen for custom event to update badge immediately
    const handlePickupNotificationRead = () => {
      setUnreadPickupNotifications(prev => Math.max(0, prev - 1));
    };
    
    window.addEventListener('pickupNotificationRead', handlePickupNotificationRead);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('pickupNotificationRead', handlePickupNotificationRead);
    };
  }, [userRole]);

  // Fetch user notifications (pen assignments, etc.)
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        // Fetch unread count
        const countResponse = await axios.get(`${API}/user-notifications/unread-count`, getAuthHeader());
        setUnreadNotifications(countResponse.data.count || 0);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    
    // Listen for custom events for notification updates
    const handleNotificationRead = () => {
      setUnreadNotifications(prev => Math.max(0, prev - 1));
    };
    
    const handleAllNotificationsRead = () => {
      setUnreadNotifications(0);
    };
    
    window.addEventListener('notificationRead', handleNotificationRead);
    window.addEventListener('allNotificationsRead', handleAllNotificationsRead);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationRead', handleNotificationRead);
      window.removeEventListener('allNotificationsRead', handleAllNotificationsRead);
    };
  }, []);

  const fetchFullNotifications = async () => {
    try {
      const response = await axios.get(`${API}/user-notifications`, getAuthHeader());
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleBellClick = () => {
    if (!showNotifications) {
      fetchFullNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/user-notifications/${notificationId}/read`, {}, getAuthHeader());
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadNotifications(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/user-notifications/mark-all-read`, {}, getAuthHeader());
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadNotifications(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // If user is not approved, show pending approval message
  if (!isApproved && userRole !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full mx-4">
          <div className="card p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pending Approval
            </h2>
            <p className="text-slate-600 mb-6">
              Your account is awaiting approval from an administrator.
            </p>
            <button
              onClick={onLogout}
              className="w-full bg-slate-900 text-white py-2.5 rounded-md font-medium hover:bg-slate-800 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper to check if user has restricted access to a tab
  const isTabRestricted = (tabPath) => {
    const restrictedTabs = user?.restricted_tabs || [];
    if (restrictedTabs.length === 0) return false;
    const pathToKey = {
      '/customer-accounts': 'customer-accounts-sheet',
      '/invoices': 'invoices',
      '/users': 'users',
      '/payroll': 'payroll'
    };
    const key = pathToKey[tabPath];
    return key ? restrictedTabs.includes(key) : false;
  };

  const closeSidebar = () => setSidebarOpen(false);

  // Helper function to check if user can access a specific path
  // For staff users: allowed_tabs contains ADDITIONAL tabs beyond defaults
  // Default staff tabs are always accessible, allowed_tabs adds more
  const DEFAULT_STAFF_TABS = [
    '/pen-production', '/production', '/inventory', '/empty-feed-bags',
    '/order-notifications', '/forms', '/timesheet', 
    '/messages', '/payslip', '/directory', '/profile'
  ];
  
  const canAccessTab = (path) => {
    const userAllowedTabs = user?.allowed_tabs || [];
    
    // For staff role: default tabs + any additional granted tabs
    if (user?.role === 'staff') {
      // Default tabs are always accessible
      if (DEFAULT_STAFF_TABS.includes(path)) return true;
      // Additional tabs require explicit grant
      return userAllowedTabs.includes(path);
    }
    
    // For other roles: if no restrictions set, allow access
    if (!userAllowedTabs || userAllowedTabs.length === 0) return true;
    // Check if this path is in the allowed tabs
    return userAllowedTabs.includes(path);
  };

  // Build navigation structure based on user role
  const buildNavigation = () => {
    const navStructure = [];

    // CUSTOMER: Place Order, Customer Order Pick-up, Customer Account Records (own data only), Messages
    if (userRole === 'customer') {
      navStructure.push({ type: 'item', item: { path: '/customer-place-order', label: 'Place Order', icon: ShoppingCart } });
      navStructure.push({ type: 'item', item: { path: '/order-request', label: 'My Order Requests', icon: ClipboardList } });
      navStructure.push({ type: 'item', item: { path: '/customer-order-pickup', label: 'My Pickups', icon: ShoppingBag, badge: unreadPickupNotifications > 0 ? unreadPickupNotifications : null } });
      navStructure.push({ type: 'item', item: { path: '/customer-account-records', label: 'My Account', icon: BookOpen } });
      navStructure.push({ type: 'item', item: { path: '/customer-messages', label: 'Messages', icon: MessageSquare, badge: unreadMessagesCount > 0 ? unreadMessagesCount : null } });
      navStructure.push({ type: 'item', item: { path: '/profile', label: 'My Profile', icon: UserCircle } });
      return navStructure;
    }

    // DISTRIBUTOR: Limited access with possible restrictions
    // DistributorTester gets full access including Payments
    // Other distributors: No Payments, but get Empty Feed Bags
    if (userRole === 'distributor') {
      const isDistributorTester = user?.username === 'DistributorTester';
      
      if (canAccessTab('/order-request')) {
        navStructure.push({ type: 'item', item: { path: '/order-request', label: 'Customer Order Request', icon: ClipboardList, badge: pendingOrderRequestsCount > 0 ? pendingOrderRequestsCount : null } });
      }
      
      // Customer Order Pick-up (shows only affiliated customers)
      if (canAccessTab('/customer-order-pickup')) {
        navStructure.push({ type: 'item', item: { path: '/customer-order-pickup', label: 'Customer Order Pick-up', icon: ShoppingBag } });
      }
      
      // Order Pick-up Summary (shows only affiliated customers)
      if (canAccessTab('/order-pickup-summary')) {
        navStructure.push({ type: 'item', item: { path: '/order-pickup-summary', label: 'Order Pick-up Summary', icon: Package } });
      }
      
      // Distributor Commission Ledger (view-only for distributors)
      if (canAccessTab('/distributor-commission')) {
        navStructure.push({ type: 'item', item: { path: '/distributor-commission', label: 'My Commission Ledger', icon: Receipt } });
      }
      
      // Customer Account Records (view-only, affiliated customers only)
      if (canAccessTab('/customer-account-records')) {
        navStructure.push({ type: 'item', item: { path: '/customer-account-records', label: 'Customer Account Records', icon: BookOpen } });
      }
      
      // All Distributors get Empty Feed Bags (if not restricted)
      if (canAccessTab('/empty-feed-bags')) {
        navStructure.push({ type: 'item', item: { path: '/empty-feed-bags', label: 'Empty Feed Bags', icon: PackageOpen } });
      }
      if (canAccessTab('/order-notifications')) {
        navStructure.push({ type: 'item', item: { path: '/order-notifications', label: 'Order Notification To Farm', icon: Bell, badge: pendingOrdersCount > 0 ? pendingOrdersCount : null } });
      }
      if (canAccessTab('/messages')) {
        navStructure.push({ type: 'item', item: { path: '/messages', label: 'Messages', icon: MessageSquare } });
      }
      // Reports → Available Stock (Management & Distributor reports)
      navStructure.push({ type: 'group', label: 'Reports', icon: BarChart3, items: [
        { path: '/distributor-stock-reports', label: 'Available Stock', icon: Package, badge: farmOpsUnreadCount > 0 ? farmOpsUnreadCount : null }
      ] });
      navStructure.push({ type: 'item', item: { path: '/profile', label: 'My Profile', icon: UserCircle } });
      return navStructure;
    }

    // VET_DOCTOR: Vet Doctor's Report (under Reports group) + Pay Slip + Messages + Profile
    if (userRole === 'vet_doctor') {
      navStructure.push({
        type: 'group',
        label: 'Reports',
        icon: BarChart3,
        items: [
          { path: '/vet-reports', label: "Vet Doctor's Report", icon: Stethoscope }
        ]
      });
      navStructure.push({ type: 'item', item: { path: '/payslip', label: 'Pay Slip', icon: NairaIcon } });
      if (canAccessTab('/messages')) {
        navStructure.push({ type: 'item', item: { path: '/messages', label: 'Messages', icon: MessageSquare } });
      }
      navStructure.push({ type: 'item', item: { path: '/profile', label: 'My Profile', icon: UserCircle } });
      return navStructure;
    }

    // AUDITOR: Auditor's Report (under Reports group) + Pay Slip + Messages + Profile
    if (userRole === 'auditor') {
      navStructure.push({
        type: 'group',
        label: 'Reports',
        icon: BarChart3,
        items: [
          { path: '/auditor-reports', label: "Auditor's Report", icon: ClipboardCheck }
        ]
      });
      navStructure.push({ type: 'item', item: { path: '/payslip', label: 'Pay Slip', icon: NairaIcon } });
      navStructure.push({ type: 'item', item: { path: '/messages', label: 'Messages', icon: MessageSquare } });
      navStructure.push({ type: 'item', item: { path: '/profile', label: 'My Profile', icon: UserCircle } });
      return navStructure;
    }

    // SUPPORT_STAFF: Pay Slip by default, plus any Admin-assigned tabs
    if (userRole === 'support_staff') {
      // Check for Admin-assigned tabs (stored in allowed_tabs)
      const assignedTabs = user?.allowed_tabs || [];
      
      // Production Group (if assigned)
      const productionItems = [];
      if (assignedTabs.includes('/pen-floor-production')) productionItems.push({ path: '/pen-floor-production', label: 'Pen Floor Daily Egg Production & Mortalities', icon: Egg });
      if (productionItems.length > 0) {
        navStructure.push({ type: 'group', label: 'Production', icon: Factory, items: productionItems });
      }
      
      // Inventory Group (if assigned)
      const inventoryItems = [];
      if (assignedTabs.includes('/chicken-inventory')) inventoryItems.push({ path: '/chicken-inventory', label: 'Chicken Demographic Records', icon: Bird });
      if (assignedTabs.includes('/chicken-census')) inventoryItems.push({ path: '/chicken-census', label: 'Chicken Census', icon: Users });
      if (assignedTabs.includes('/chicken-feed-audit')) inventoryItems.push({ path: '/chicken-feed-audit', label: 'Chicken Feed Audit', icon: Wheat });
      if (assignedTabs.includes('/frozen-chicken-count')) inventoryItems.push({ path: '/frozen-chicken-count', label: 'Frozen Chicken Count', icon: Snowflake });
      if (inventoryItems.length > 0) {
        navStructure.push({ type: 'group', label: 'Inventory', icon: Boxes, items: inventoryItems });
      }
      
      // Individual items (if assigned)
      if (assignedTabs.includes('/order-notifications')) navStructure.push({ type: 'item', item: { path: '/order-notifications', label: 'Order Notification', icon: Bell, badge: pendingOrdersCount > 0 ? pendingOrdersCount : null } });
      if (assignedTabs.includes('/order-request')) navStructure.push({ type: 'item', item: { path: '/order-request', label: 'Order Request', icon: ShoppingCart } });
      // Forms is always available to support staff (Farm Guidelines must be visible to all staff)
      navStructure.push({ type: 'item', item: { path: '/forms', label: 'Forms', icon: FileStack } });
      // Daily Farm Operations Report (available to all staff)
      navStructure.push({ type: 'group', label: 'Reports', icon: BarChart3, items: [
        { path: '/daily-farm-operations-report', label: 'Daily Farm Operations Report', icon: ClipboardList, badge: farmOpsUnreadCount > 0 ? farmOpsUnreadCount : null }
      ] });
      if (assignedTabs.includes('/timesheet')) navStructure.push({ type: 'item', item: { path: '/timesheet', label: 'Time Sheet', icon: Clock } });
      if (assignedTabs.includes('/messages')) navStructure.push({ type: 'item', item: { path: '/messages', label: 'Messages', icon: MessageSquare } });
      if (assignedTabs.includes('/customers')) navStructure.push({ type: 'item', item: { path: '/customers', label: 'Customers', icon: Users } });
      if (assignedTabs.includes('/directory')) navStructure.push({ type: 'item', item: { path: '/directory', label: 'Directory', icon: BookUser } });
      
      // Pay Slip is always available for Support Staff (default)
      navStructure.push({ type: 'item', item: { path: '/payslip', label: 'Pay Slip', icon: NairaIcon } });
      navStructure.push({ type: 'item', item: { path: '/profile', label: 'My Profile', icon: UserCircle } });
      return navStructure;
    }

    // STAFF: Fixed tabs with possible restrictions
    if (userRole === 'staff') {
      // Production Group
      const productionItems = [];
      if (canAccessTab('/pen-floor-production')) productionItems.push({ path: '/pen-floor-production', label: 'Pen Floor Daily Egg Production & Mortalities', icon: Egg });
      if (productionItems.length > 0) {
        navStructure.push({ type: 'group', label: 'Production', icon: Factory, items: productionItems });
      }
      
      // Inventory Group
      const inventoryItems = [];
      const hasAssignedPens = user?.assigned_pens && user.assigned_pens.length > 0;
      if (canAccessTab('/chicken-inventory')) inventoryItems.push({ path: '/chicken-inventory', label: 'Chicken Demographic Records', icon: Bird });
      if (canAccessTab('/chicken-census') || hasAssignedPens) inventoryItems.push({ path: '/chicken-census', label: 'Chicken Census', icon: Users });
      if (canAccessTab('/chicken-feed-audit')) inventoryItems.push({ path: '/chicken-feed-audit', label: 'Chicken Feed Audit', icon: Wheat });
      if (canAccessTab('/frozen-chicken-count')) inventoryItems.push({ path: '/frozen-chicken-count', label: 'Frozen Chicken Count', icon: Snowflake });
      if (inventoryItems.length > 0) {
        navStructure.push({ type: 'group', label: 'Inventory', icon: Boxes, items: inventoryItems });
      }
      
      // Individual items for staff
      if (canAccessTab('/order-notifications')) navStructure.push({ type: 'item', item: { path: '/order-notifications', label: 'Order Notification', icon: Bell, badge: pendingOrdersCount > 0 ? pendingOrdersCount : null } });
      if (canAccessTab('/customer-order-pickup')) navStructure.push({ type: 'item', item: { path: '/customer-order-pickup', label: 'Customer Order Pick-up', icon: ShoppingBag } });
      // Forms is always available to staff (Farm Guidelines must be visible to all staff)
      navStructure.push({ type: 'item', item: { path: '/forms', label: 'Forms', icon: FileStack } });
      // Daily Farm Operations Report (available to all staff)
      navStructure.push({ type: 'group', label: 'Reports', icon: BarChart3, items: [
        { path: '/daily-farm-operations-report', label: 'Daily Farm Operations Report', icon: ClipboardList, badge: farmOpsUnreadCount > 0 ? farmOpsUnreadCount : null }
      ] });
      if (canAccessTab('/timesheet')) navStructure.push({ type: 'item', item: { path: '/timesheet', label: 'Time Sheet', icon: Clock } });
      if (canAccessTab('/messages')) navStructure.push({ type: 'item', item: { path: '/messages', label: 'Messages', icon: MessageSquare } });
      if (canAccessTab('/payslip')) navStructure.push({ type: 'item', item: { path: '/payslip', label: 'Pay Slip', icon: NairaIcon } });
      if (canAccessTab('/directory')) navStructure.push({ type: 'item', item: { path: '/directory', label: 'Directory', icon: BookUser } });
      navStructure.push({ type: 'item', item: { path: '/profile', label: 'My Profile', icon: UserCircle } });
      
      return navStructure;
    }

    // ADMIN and MANAGER
    // Dashboard
    navStructure.push({ type: 'item', item: { path: '/', label: 'Dashboard', icon: LayoutDashboard } });
    
    // Production Group (Admin only for Daily Egg Production by Pen)
    navStructure.push({
      type: 'group',
      label: 'Production',
      icon: Factory,
      items: [
        { path: '/pen-floor-production', label: 'Pen Floor Daily Egg Production & Mortalities', icon: Egg },
        { path: '/daily-egg-production', label: 'Daily Egg Production by Pen', icon: Egg },
        { path: '/production-reports', label: 'Production Reports', icon: BarChart3 },
        ...(userRole === 'admin' ? [{ path: '/productions-edit', label: 'Productions Edit', icon: Wrench }] : [])
      ]
    });
    
    // Inventory Group
    navStructure.push({
      type: 'group',
      label: 'Inventory',
      icon: Boxes,
      items: [
        { path: '/chicken-inventory', label: 'Chicken Demographic Records', icon: Bird },
        { path: '/chicken-census', label: 'Chicken Census', icon: Users },
        { path: '/chicken-feed-audit', label: 'Chicken Feed Audit', icon: Wheat },
        { path: '/frozen-chicken-count', label: 'Frozen Chicken Count', icon: Snowflake }
      ]
    });
    
    // Sales Group
    const salesItems = [
      { path: '/order-notifications', label: 'Order Notification To Farm', icon: Bell, badge: pendingOrdersCount > 0 ? pendingOrdersCount : null },
      { path: '/order-request', label: 'Customer Order Request', icon: ClipboardList, badge: pendingOrderRequestsCount > 0 ? pendingOrderRequestsCount : null },
      { path: '/customer-order-pickup', label: 'Customer Order Pick-up', icon: ShoppingBag },
      { path: '/order-pickup-summary', label: 'Order Pick-up Summary', icon: Package },
      ...(userRole === 'admin' ? [{ path: '/pickup-analytics', label: 'Pick-up Analytics', icon: BarChart3 }] : []),
      ...((userRole === 'admin' || userRole === 'manager') ? [{ path: '/sales-analysis', label: 'Sales Analysis', icon: TrendingUp }] : []),
      { path: '/price', label: 'Price', icon: Tag }
    ];
    navStructure.push({
      type: 'group',
      label: 'Sales',
      icon: Store,
      items: salesItems
    });
    
    // Accounts Group
    const accountsItems = [
      { path: '/customer-account-records', label: 'Customer Account Records', icon: BookOpen }
    ];
    
    // Bank Accounts Reconciliation
    // Admin: always. Manager: only if explicitly granted via Manage Tab Access (view-only, enforced server-side)
    const legacyReconciliationManagers = ['Waliu Aina', 'Rashidat Aina'];
    const managerTabs = user?.allowed_tabs || [];
    const canBankTab = (path) => userRole === 'admin' ||
      (userRole === 'manager' && (managerTabs.includes(path) || legacyReconciliationManagers.includes(user?.name)));
    
    if (canBankTab('/access-bank')) accountsItems.push({ path: '/access-bank', label: 'Access Bank', icon: Scale, badge: unmatchedDepositsCount > 0 ? unmatchedDepositsCount : null });
    if (canBankTab('/zenith-bank')) accountsItems.push({ path: '/zenith-bank', label: 'Zenith Bank', icon: Scale, badge: unmatchedDepositsCount > 0 ? unmatchedDepositsCount : null });
    if (canBankTab('/other-bank')) accountsItems.push({ path: '/other-bank', label: 'Other Bank', icon: Scale, badge: unmatchedDepositsCount > 0 ? unmatchedDepositsCount : null });
    
    navStructure.push({
      type: 'group',
      label: 'Accounts',
      icon: CreditCard,
      items: accountsItems
    });
    
    // Payroll Group
    const payrollItems = [];
    // Payroll Management is admin-only (managers cannot access)
    if (userRole === 'admin') {
      payrollItems.push({ path: '/payroll', label: 'Payroll Management', icon: Banknote });
    }
    payrollItems.push({ path: '/payslip', label: 'Pay Slip', icon: NairaIcon });
    payrollItems.push({ path: '/distributor-commission', label: 'Distributor Commission Ledger', icon: Receipt });
    navStructure.push({
      type: 'group',
      label: 'Payroll',
      icon: Banknote,
      items: payrollItems
    });
    
    // Other tabs (not grouped)
    navStructure.push({ type: 'item', item: { path: '/customers', label: 'Customers', icon: Users } });
    navStructure.push({ type: 'item', item: { path: '/distributors', label: 'Distributor/Vet Doctor', icon: Truck } });
    navStructure.push({
      type: 'group',
      label: 'Reports',
      icon: BarChart3,
      items: [
        { path: '/reports', label: 'Business Report', icon: BarChart3 },
        { path: '/vet-reports', label: "Vet Doctor's Report", icon: Stethoscope },
        { path: '/auditor-reports', label: "Auditor's Report", icon: ClipboardCheck },
        { path: '/daily-farm-operations-report', label: 'Daily Farm Operations Report', icon: ClipboardList, badge: farmOpsUnreadCount > 0 ? farmOpsUnreadCount : null }
      ]
    });
    // Forms group with per-form-type pending badges; parent badge = sum of all
    const formTypeDefs = [
      { ft: 'day_off_request', path: '/forms?type=day-off-request', label: 'Day-Off Request', icon: Calendar },
      { ft: 'incident_report', path: '/forms?type=incident-report', label: 'Incident Report', icon: AlertTriangle },
      { ft: 'loan_request', path: '/forms?type=loan-request', label: 'Loan Request', icon: CircleDollarSign },
      { ft: 'third_party_payment', path: '/forms?type=third-party-payment', label: 'Third-Party Salary Pay', icon: CreditCard },
      { ft: 'personal_items_inventory', path: '/forms?type=personal-items-inventory', label: 'Personal Items Inventory', icon: Package },
      { ft: 'day_off_commutation', path: '/forms?type=day-off-commutation', label: 'Day-Off Commutation', icon: RefreshCw },
      { ft: 'staff_resignation', path: '/forms?type=staff-resignation', label: 'Staff Resignation', icon: UserMinus },
      { ft: 'job_application', path: '/forms?type=job-application', label: 'Job Application', icon: UserPlus },
    ];
    const formItems = [
      { path: '/forms', label: 'All Forms', icon: FileStack },
      ...formTypeDefs.map(f => ({
        path: f.path,
        label: f.label,
        icon: f.icon,
        badge: pendingFormsByType[f.ft] > 0 ? pendingFormsByType[f.ft] : null,
      })),
    ];
    navStructure.push({ type: 'group', label: 'Forms', icon: FileStack, items: formItems, badge: pendingFormsCount > 0 ? pendingFormsCount : null });
    navStructure.push({ type: 'item', item: { path: '/timesheet', label: 'Time Sheet', icon: Clock } });
    navStructure.push({ type: 'item', item: { path: '/messages', label: 'Messages', icon: MessageSquare } });
    navStructure.push({ type: 'item', item: { path: '/directory', label: 'Directory', icon: BookUser } });
    
    // Admin only
    if (userRole === 'admin') {
      navStructure.push({ type: 'item', item: { path: '/file-manager', label: 'File Manager', icon: FolderOpen } });
      navStructure.push({ type: 'item', item: { path: '/pen-assignments', label: 'Pen Assignments', icon: Users } });
      navStructure.push({ type: 'item', item: { path: '/users', label: 'Staff Management', icon: Settings } });
      navStructure.push({ type: 'item', item: { path: '/settings', label: 'Currency', icon: Settings } });
    } else if (userRole === 'manager' && !isTabRestricted('/users')) {
      navStructure.push({ type: 'item', item: { path: '/users', label: 'Staff Management', icon: Settings } });
    }
    
    navStructure.push({ type: 'item', item: { path: '/profile', label: 'My Profile', icon: UserCircle } });
    
    return navStructure;
  };

  const navStructure = buildNavigation();

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile Menu Button */}
      <button
        data-testid="mobile-menu-button"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-900 text-white"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Desktop Sidebar Toggle Button */}
      <button
        data-testid="desktop-sidebar-toggle"
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className={`hidden lg:flex fixed top-4 z-50 p-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-all duration-300 ${
          sidebarCollapsed ? 'left-4' : 'left-[252px]'
        }`}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 bg-slate-900 text-slate-300 transform transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${sidebarCollapsed ? 'lg:w-0 lg:overflow-hidden' : 'w-64'}`}
      >
        <div className={`flex flex-col h-screen max-h-screen w-64 ${sidebarCollapsed ? 'lg:opacity-0' : 'lg:opacity-100'} transition-opacity duration-300`}>
          {/* Logo */}
          <div className="p-4 border-b border-slate-800 flex-shrink-0">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg" 
                alt="Wilyrash Farm Logo" 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Wilyrash Farm
                </h1>
                <p className="text-xs text-slate-400">Farm Management</p>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="px-4 py-2 bg-slate-800/50 flex-shrink-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto min-h-0 pb-20" style={{ WebkitOverflowScrolling: 'touch' }}>
            {navStructure.map((navItem, index) => {
              if (navItem.type === 'group') {
                return (
                  <NavGroup
                    key={`group-${index}`}
                    label={navItem.label}
                    icon={navItem.icon}
                    items={navItem.items}
                    location={location}
                    onNavigate={closeSidebar}
                    badge={navItem.badge || null}
                  />
                );
              } else {
                return (
                  <NavItem
                    key={navItem.item.path}
                    item={navItem.item}
                    location={location}
                    onNavigate={closeSidebar}
                  />
                );
              }
            })}
            
            {/* Logout */}
            <button
              data-testid="logout-button"
              onClick={onLogout}
              className="flex items-center gap-3 px-4 py-2.5 w-full text-sm font-medium rounded-md text-slate-300 hover:bg-slate-800 hover:text-white transition-colors mt-2 mb-4"
            >
              <LogOut className="w-5 h-5" strokeWidth={2} />
              Logout
            </button>
          </nav>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-0' : ''}`}>
        {/* Top Header Bar with Notification Bell */}
        <div className={`fixed top-0 right-0 z-20 bg-white/80 backdrop-blur-sm border-b border-slate-200 px-4 py-2 flex items-center justify-end gap-3 transition-all duration-300 ${
          sidebarCollapsed ? 'left-0 lg:left-16' : 'left-0 lg:left-64'
        }`}>
          {/* Notification Bell */}
          <div className="relative">
            <button
              data-testid="notification-bell"
              onClick={handleBellClick}
              className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
              title="Notifications"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 top-12 w-80 md:w-96 bg-white rounded-lg shadow-xl border border-slate-200 z-50 max-h-[70vh] overflow-hidden">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                  <h3 className="font-semibold text-slate-800">Notifications</h3>
                  {unreadNotifications > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                      data-testid="mark-all-read"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto max-h-[50vh]">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">
                      <Bell className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        data-testid={`notification-item-${notification.id}`}
                        className={`p-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
                          !notification.is_read ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => !notification.is_read && markNotificationRead(notification.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                            !notification.is_read ? 'bg-blue-500' : 'bg-transparent'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${!notification.is_read ? 'text-slate-900' : 'text-slate-600'}`}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {/* View All Link */}
                <Link
                  to="/notification-history"
                  className="block p-3 text-center text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-t border-slate-100"
                  onClick={() => setShowNotifications(false)}
                >
                  View All Notifications
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Click outside to close notifications */}
        {showNotifications && (
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowNotifications(false)}
          />
        )}

        <div className={`pt-16 px-2 pb-4 md:pt-14 md:px-6 md:pb-6 lg:p-8 lg:pt-16 xl:p-10 xl:pt-16 min-w-0 ${sidebarCollapsed ? 'lg:pl-16' : ''}`}>{children}</div>
      </main>
    </div>
  );
};

export default Layout;
